import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  InputGroup,
  InputRightAddon,
  NumberInput,
  NumberInputField,
  SimpleGrid,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormLabelWithInfo } from "../../../../../common/input/FormLabelWithInfo";
import { SelectableCard } from "../../../../../common/input/SelectableCard";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  ProcedureType,
  Regulation,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import {
  procedureProvisionKey,
  procedureTypeOptionsFor,
  requiresProcedureJustification,
  type ProcedureTypeOption,
} from "../utils/publicProcurementOptions";
import { allowsNegotiation, isDpsCompetition, isDpsScheme, isTwoStageProcedure } from "../utils/procedure";
import { isAtOrAboveEea, minCandidatesWhenCapped } from "../utils/karens";
import { useSub } from "../../../../../common/subscription/useSub";
import { CandidateLimitsField } from "../components/CandidateLimitsField";
import { DpsSchemeFields } from "../components/DpsSchemeFields";
import { NegotiationTermsField } from "../components/NegotiationTermsField";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";
import { SubmissionLanguagesField } from "../components/SubmissionLanguagesField";
import { MilestonePlanCard } from "../components/MilestonePlanCard";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ProcedureStep = ({ eventId, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const sub = useSub();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const validityError = fieldError("publicContext.eFormsNoticeData.tenderValidityMonths");

  const validityMonths = publicContext?.eFormsNoticeData?.tenderValidityMonths ?? null;
  const [validityStr, setValidityStr] = useState<string>(validityMonths != null ? String(validityMonths) : "");

  const isSaving = (field: string) => {
    const s = fieldStatus(field);
    return s === "saving" || s === "pending";
  };

  const procedureType = publicContext?.procedureType ?? "";
  const regulation = publicContext?.regulation ?? null;
  const procedureOptions = procedureTypeOptionsFor(regulation);
  // Two-stage procedures need the regulation's EØS tier — del III under FOA, del II under
  // forsyningsforskriften. Below it, del II's FOA sibling (begrenset tilbudskonkurranse) has
  // different rules and is a separate, later delivery.
  const overEeaThreshold = isAtOrAboveEea(regulation, publicContext?.foaPart);
  const meetsPartRequirement = (value: ProcedureType) => !isTwoStageProcedure(value) || overEeaThreshold;
  const optionEnabled = (option: ProcedureTypeOption) => option.enabled && meetsPartRequirement(option.value);
  const eeaTierLabel =
    regulation === Regulation.Forsyningsforskriften
      ? t("Requires part II or above (over the EEA threshold)")
      : t("Requires FOA part III (above the EEA threshold)");
  const optionDisabledLabel = (option: ProcedureTypeOption) =>
    !option.enabled ? t("Coming soon") : eeaTierLabel;

  // The dynamiske innkjøpsordning on offer is FOA § 26-5 to § 26-7 throughout — its notice, its
  // intake and its purchases. Forsyningsforskriften has its own, on its own rules, and offering the
  // FOA one under it would announce the wrong regime; that is a delivery of its own.
  const dpsSupported = regulation !== Regulation.Forsyningsforskriften;
  const dpsSelectable = sub.hasDpsAccess && dpsSupported && overEeaThreshold;
  const dpsDisabledLabel = !sub.hasDpsAccess || !dpsSupported ? t("Coming soon") : eeaTierLabel;

  // FOA § 26-7 settled the procedure at the announcement — only this purchase's terms are left.
  if (isDpsCompetition(publicContext?.procedureType)) {
    return (
      <Box pb="8" pt="10">
        <Box borderWidth="1px" borderColor="smBorder" bg="smBackground" rounded="lg" p="5" mb="8">
          <Text fontWeight="semibold" fontSize="sm" color="smText">
            {t("Competition under a dynamic purchasing system")}
          </Text>
          <Text fontSize="xs" color="smMuted" mt="1">
            {publicContext?.dpsCompetition?.schemeTitle
              ? t("Part of {{scheme}}. Every supplier qualified when you open it is invited.", {
                  scheme: publicContext.dpsCompetition.schemeTitle,
                })
              : t("Every supplier qualified when you open it is invited.")}
          </Text>
        </Box>

        <MilestonePlanCard event={state} />

        <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
      </Box>
    );
  }

  return (
    <Box pb="8" pt="10">
      <FormControl mb="8">
        <FormLabel>
          {t("Procedure type")} {isSaving("publicContext.procedureType") && <Spinner size="xs" />}
        </FormLabel>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
          {procedureOptions.map((option) => {
            const provisionKey = procedureProvisionKey(regulation, option.value);
            return (
            <SelectableCard
              key={option.value}
              isSelected={procedureType === option.value}
              isDisabled={!optionEnabled(option)}
              disabledLabel={optionDisabledLabel(option)}
              onClick={() => {
                if (optionEnabled(option)) {
                  updateField({ publicContext: { procedureType: option.value } });
                }
              }}
            >
              <Text fontWeight="semibold" fontSize="sm" color={option.enabled ? "smText" : "smMuted"}>
                {t(option.value)}
              </Text>
              <Text fontSize="xs" color="smMuted" mt="1">
                {t(option.descriptionKey)}
              </Text>
              {provisionKey && (
                <Text fontSize="xs" color="smMuted" mt="2" fontStyle="italic">
                  {t(provisionKey)}
                </Text>
              )}
            </SelectableCard>
            );
          })}
          <SelectableCard
            key="dynamic-purchasing-system"
            isSelected={procedureType === ProcedureType.DynamicPurchasingSystem}
            isDisabled={!dpsSelectable}
            disabledLabel={dpsDisabledLabel}
            gridColumn={{ md: "1 / -1" }}
            onClick={() => {
              if (dpsSelectable) {
                updateField({ publicContext: { procedureType: ProcedureType.DynamicPurchasingSystem } });
              }
            }}
          >
            <Text
              fontWeight="semibold"
              fontSize="sm"
              color={dpsSelectable ? "smText" : "smMuted"}
            >
              {t("Dynamic purchasing system (DPS)")}
            </Text>
            <Text fontSize="xs" color="smMuted" mt="1">
              {t("Continuous qualification of suppliers")}
            </Text>
          </SelectableCard>
        </SimpleGrid>
      </FormControl>

      <FormControl
        mb="8"
        isInvalid={!!validityError}
        display={isTwoStageProcedure(publicContext?.procedureType) ? "none" : undefined}
      >
        <FormLabelWithInfo
          tooltip={t("Specifies how long suppliers are bound by their bid. Common practice is 3 or 6 months.")}
          suffix={isSaving("publicContext.eFormsNoticeData.tenderValidityMonths") && <Spinner size="xs" ml="1" />}
        >
          {t("Tender validity period")}
        </FormLabelWithInfo>
        <InputGroup maxW="250px">
          <NumberInput
            value={validityStr}
            clampValueOnBlur={false}
            keepWithinRange={false}
            w="full"
            onChange={(valueAsString, valueAsNumber) => {
              setValidityStr(valueAsString);
              if (!isNaN(valueAsNumber)) {
                updateField(
                  { publicContext: { eFormsNoticeData: { tenderValidityMonths: valueAsNumber } } },
                  { debounceMs: 300 },
                );
              }
            }}
          >
            <NumberInputField borderRightRadius="0" />
          </NumberInput>
          {/* t('month_one') t('month_other') */}
          <InputRightAddon color="smMuted" fontSize="sm">
            {t("month", { count: Number(validityStr) || 1 })}
          </InputRightAddon>
        </InputGroup>
        {validityError && (
          <FormErrorMessage>{formatFieldError(validityError)}</FormErrorMessage>
        )}
      </FormControl>

      {isDpsScheme(publicContext?.procedureType) && (
        <DpsSchemeFields
          eventId={eventId}
          usage={publicContext?.dynamicPurchasingSystem ?? null}
          scheme={publicContext?.dpsScheme ?? null}
          isEditable={state.status === "DRAFT"}
        />
      )}

      {/* The stage-1 deadline (frist for forespørsel om å delta) is set and confirmed directly
          on its node in the milestone plan, like the bid deadline for single-stage. */}
      <MilestonePlanCard event={state} />

      {/* Nedvalg follows the plan: first when the request phase ends, then how many proceed.
          § 26-4 forbids capping a DPS — every qualified supplier is admitted. */}
      {isTwoStageProcedure(publicContext?.procedureType) && !isDpsScheme(publicContext?.procedureType) && (
        <Box mb="8">
          <CandidateLimitsField
            eventId={eventId}
            current={publicContext?.candidateLimits ?? null}
            statutoryMin={minCandidatesWhenCapped(
              publicContext?.regulation,
              publicContext?.foaPart,
              publicContext?.procedureType,
            )}
          />
        </Box>
      )}

      {allowsNegotiation(publicContext?.procedureType) && (
        <Box mb="8">
          <NegotiationTermsField
            eventId={eventId}
            justification={publicContext?.procedureJustification ?? null}
            noNegotiationNecessary={publicContext?.noNegotiationNecessary ?? null}
            requiresJustification={requiresProcedureJustification(regulation)}
          />
        </Box>
      )}

      <Box mb="8">
        <SubmissionLanguagesField eventId={eventId} />
      </Box>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
